import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import {
  Grid,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  TextField
} from '@mui/material';
import theme from 'src/theme';
import Loader from 'src/common/Loader';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';
import { addNewTyre, updateTyreDetails } from 'src/redux/tyreSlice';
import { getAllSites } from 'src/redux/siteSlice';
import InputField from 'src/common/formFields/InputField';
import FMSModal, { FMSModalTitle } from 'src/common/FMSModal';
import SelectField from 'src/common/formFields/SelectField';
import DateFilterField from 'src/common/formFields/DateFilterField';
import {
  tyreFormModel,
  tyreFormInitialValues,
  tyreValidationSchema
} from './tyreFormModal';

const NewTyres = ({ open, handleClose, initialValues }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { allSites } = useSelector((state) => state.sites);

  const formInitValues = initialValues || tyreFormInitialValues;

  const {
    formId,
    formField: {
      site,
      tyre,
      travelled,
      figmentDate,
      tyreBrand,
      purchaseDate
    }
  } = tyreFormModel;

  useEffect(() => {
    if (open) {
      dispatch(getAllSites());
    }
  }, [open, dispatch]);

  const onSubmit = async (values, actions) => {
    setLoading(true);
    try {
      if (initialValues) {
        await dispatch(updateTyreDetails({
          ...values,
          id: initialValues.id
        })).unwrap();
      } else {
        await dispatch(addNewTyre(values)).unwrap();
      }

      handleClose();
      dispatch(
        setSuccessInfo({
          successMsg: initialValues
            ? 'Tyre details updated successfully!'
            : 'New tyre created successfully!'
        })
      );
    } catch (error) {
      actions.setSubmitting(false);
      dispatch(setErrorInfo({ errorMsg: error.error_message }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FMSModal open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <FMSModalTitle
        title={initialValues ? 'UPDATE TYRE' : 'ADD NEW TYRE'}
        onCancel={handleClose}
      />
      <Divider />

      <Formik
        initialValues={formInitValues}
        validationSchema={tyreValidationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <>
            {loading ? (
              <Loader />
            ) : (
              <Form id={formId}>
                <DialogContent sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={tyre.name}
                        label={tyre.label}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={travelled.name}
                        label={travelled.label}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <DateFilterField
                        name={purchaseDate.name}
                        label={purchaseDate.label}
                        hideClose
                        value={values[purchaseDate.name] || null}
                        onDateChange={(newValue) => setFieldValue(purchaseDate.name, newValue)}
                        pickerProps={{
                          renderInput: (params) => (
                            <TextField
                              {...params}
                              fullWidth
                              size="medium"
                            />
                          )
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={tyreBrand.name}
                        label={tyreBrand.label}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <DateFilterField
                        name={figmentDate.name}
                        label={figmentDate.label}
                        hideClose
                        value={values[figmentDate.name] || null}
                        onDateChange={(newValue) => setFieldValue(figmentDate.name, newValue)}
                        pickerProps={{
                          renderInput: (params) => (
                            <TextField
                              {...params}
                              fullWidth
                              size="medium"
                            />
                          )
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={site.name}
                        label={site.label}
                        data={allSites}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </DialogContent>

                <DialogActions
                  sx={{
                    p: 2.5,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    bgcolor: '#F9FAFB'
                  }}
                >
                  <Button
                    onClick={handleClose}
                    variant="contained"
                    color="primary"
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    variant="contained"
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3
                    }}
                  >
                    {initialValues ? 'Update Tyre' : 'Create Tyre'}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </>
        )}
      </Formik>
    </FMSModal>
  );
};

NewTyres.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object
};

export default NewTyres;
